import { ArrowLeftRight, Info, Package, ShoppingCart } from "lucide-react"
import { Drawer, StatusPill, EmptyState, ErrorState, formatDateTime } from "procurement/design"
import { useGetBookInventoryDemandClassesQuery } from "shared/redux/endpoints/procurementItems"
import { Button } from "../../ui"
import BookThumb from "./BookThumb"
import { at, classTone, formatQty, formatStartDay, isUnmappedRow, num } from "../../bookInventory/model"

const Stat = ({ label, value, tone }) => (
  <div className="bi-stat">
    <dt>{label}</dt>
    <dd className={tone ? `bi-gap bi-gap--${tone}` : undefined}>{value}</dd>
  </div>
)

const books = (value) => (value == null ? "—" : `${formatQty(value)} books`)

const shortageTone = (value) => (value == null ? undefined : num(value) > 0 ? "neg" : "zero")

const humanize = (value) =>
  value ? String(value).charAt(0) + String(value).slice(1).toLowerCase().replace(/_/g, " ") : "—"

const students = (value) => (value == null ? null : <span className="bi-sub">{formatQty(value)} students</span>)

// Course · level shared by every class, for the header badge.
const sharedLabel = (classes) => {
  const labels = [...new Set(classes.map((c) => [c.levelName, c.courseName].filter(Boolean).join(" · ")))]
  return labels.length === 1 ? labels[0] : ""
}

const CompactClassTable = ({ classes }) => (
  <div style={{ overflowX: "auto" }}>
    <table className="bi-class-table">
      <thead>
        <tr>
          <th>Class name</th>
          <th>Start date</th>
          <th className="is-num">Students</th>
          <th className="is-num">Books needed</th>
          <th className="is-num">Issued</th>
          <th className="is-num">Still needed</th>
        </tr>
      </thead>
      <tbody>
        {classes.map((entry, index) => (
          <tr key={`${entry.classId ?? entry.className}-${index}`}>
            <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{entry.className || "—"}</td>
            <td>{formatStartDay(entry.startDate)}</td>
            <td className="is-num">{formatQty(entry.studentCount)}</td>
            <td className="is-num">{formatQty(entry.grossNeededQty)}</td>
            <td className="is-num">{formatQty(entry.issuedQty)}</td>
            <td className="is-num">{formatQty(entry.remainingNeededQty)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

export const ClassTable = ({ classes, compact = false }) => {
  if (compact) return <CompactClassTable classes={classes} />
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="bi-class-table">
        <thead>
          <tr>
            <th>Current class</th>
            <th>Upcoming class</th>
            <th>Start date</th>
            <th>Sessions</th>
            <th>Book rule</th>
            <th className="is-num">Students</th>
            <th className="is-num">Actual +30%</th>
            <th className="is-num">Estimated +30%</th>
            <th className="is-num">Issued</th>
            <th className="is-num">Still needed</th>
            <th>Calculation</th>
            <th>OR status</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {classes.map((entry, index) => (
            <tr key={`${entry.classId ?? entry.className}-${index}`}>
              <td>
                {entry.currentClassName || "—"}
                {entry.currentClassName && (
                  <span className="bi-sub">
                    {[entry.currentClassLevel, entry.currentClassStudentCount != null && `${formatQty(entry.currentClassStudentCount)} students`]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
              </td>
              <td>
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{entry.className || "—"}</span>
                <span className="bi-sub">{[entry.courseName, entry.levelName].filter(Boolean).join(" · ")}</span>
              </td>
              <td>{formatStartDay(entry.startDate)}</td>
              <td>
                {entry.startSessionName || entry.endSessionName
                  ? `${entry.startSessionName || "—"} → ${entry.endSessionName || "—"}`
                  : "—"}
                {entry.totalHour != null && <span className="bi-sub">{formatQty(entry.totalHour)} h</span>}
              </td>
              <td>{entry.bookRule || "—"}</td>
              <td className="is-num">{formatQty(entry.studentCount)}</td>
              <td className="is-num">
                {formatQty(entry.actualBooksNeeded)}
                {students(entry.actualStudentsBuffered)}
              </td>
              <td className="is-num">
                {formatQty(entry.estimatedBooksNeeded)}
                {students(entry.estimatedStudentsBuffered)}
              </td>
              <td className="is-num">{formatQty(entry.issuedQty)}</td>
              <td className="is-num">{formatQty(entry.remainingNeededQty)}</td>
              <td>
                <StatusPill
                  tone={entry.calculationStatus === "CALCULATED" ? "success" : "warning"}
                  label={humanize(entry.calculationStatus)}
                />
              </td>
              <td>
                {entry.orderStatus === "HAS_OR" ? (
                  <>
                    <StatusPill tone="info" label="HAS_OR" />
                    <span className="bi-sub">
                      {formatQty(entry.orRequestedQty)} books · {formatQty(entry.orCount)} OR
                    </span>
                  </>
                ) : entry.orderStatus ? (
                  <StatusPill tone="neutral" label={entry.orderStatus} />
                ) : (
                  "—"
                )}
              </td>
              <td>
                <StatusPill tone={classTone(entry.daysUntilStart)} label={entry.status || "—"} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const classesWithin = (classes, days) => classes.filter((c) => c.daysUntilStart == null || Number(c.daysUntilStart) <= days)

const DemandDetailDrawer = ({ row, open, daysWindow, onClose, onCreateOrder, onViewStock, onTransfer, canCreateOrder }) => {
  const canLoad = open && row?.centerId != null && row?.itemId != null
  const { data, isFetching, isError, error, refetch } = useGetBookInventoryDemandClassesQuery(
    { centerId: row?.centerId, itemId: row?.itemId, daysWindow },
    { skip: !canLoad }
  )
  const item = data?.item
  const classes = data?.classes || []
  const total = classes.reduce((sum, c) => sum + (Number(c.remainingNeededQty) || 0), 0)
  const badge = sharedLabel(classes)
  const unmapped = row ? isUnmappedRow(row) : false
  const stock = item ? item.quantityOnHand : row?.currentStock ?? row?.stockRemaining

  return (
    <Drawer
      wide
      open={open}
      onClose={onClose}
      title={
        <div className="bi-detail-head">
          <BookThumb key={item?.imageUrl || row?.imageUrl || "none"} src={item?.imageUrl || row?.imageUrl} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Demand Detail</div>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>
              {unmapped ? "No book mapping configured" : item?.itemName || row?.itemName || "Book"}
            </h2>
            <div className="bi-detail-sub">
              {(item?.itemCode || row?.itemCode) && <span>{item?.itemCode || row?.itemCode}</span>}
              {badge && <span className="bi-chip">{badge}</span>}
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          {row?.itemId != null && (
            <Button leftIcon={<Package size={15} aria-hidden />} onClick={() => onViewStock(row)}>
              View stock
            </Button>
          )}
          {onTransfer && row?.itemId != null && (
            <Button leftIcon={<ArrowLeftRight size={15} aria-hidden />} onClick={() => onTransfer(row)}>
              Transfer
            </Button>
          )}
          {canCreateOrder && row?.itemId != null && (
            <Button
              variant="primary"
              leftIcon={<ShoppingCart size={15} aria-hidden />}
              onClick={() => onCreateOrder(row)}
            >
              Create order request
            </Button>
          )}
        </div>
      }
    >
      {row && (
        <div>
          <dl className="bi-stats">
            <Stat label="Center" value={row.centerName || `Center ${row.centerId}`} />
            <Stat label={`Actual demand +30% (${daysWindow} days)`} value={books(at(row, "actualNeeded", daysWindow))} />
            <Stat label={`Estimated demand +30% (${daysWindow} days)`} value={books(at(row, "estimatedNeeded", daysWindow))} />
            <Stat label="Current stock" value={books(stock)} />
            <Stat
              label={`Actual shortage (${daysWindow} days)`}
              value={books(at(row, "actualShortage", daysWindow))}
              tone={shortageTone(at(row, "actualShortage", daysWindow))}
            />
            <Stat
              label={`Estimated shortage (${daysWindow} days)`}
              value={books(at(row, "estimatedShortage", daysWindow))}
              tone={shortageTone(at(row, "estimatedShortage", daysWindow))}
            />
            <Stat
              label="OR status"
              value={row.orderStatus ? `${row.orderStatus}${row.orderStatus === "HAS_OR" ? ` · ${formatQty(row.orRequestedQty)} books` : ""}` : "—"}
            />
          </dl>
          {item && (
            <p className="bi-muted">
              {item.stockUpdatedAt
                ? `Stock updated ${formatDateTime(item.stockUpdatedAt)}.`
                : "This center has no stock record for this book yet."}
            </p>
          )}

          {!canLoad ? (
            <p className="bi-note">No book mapping configured for this class, so there is no class breakdown.</p>
          ) : isError ? (
            <ErrorState
              title={error?.status === 404 ? "Book not found" : "Couldn't load the classes"}
              text={error?.status === 404 ? "This book no longer exists in the catalog." : "Try again in a moment."}
              action={
                error?.status === 404 ? null : (
                  <Button size="sm" onClick={refetch}>
                    Retry
                  </Button>
                )
              }
            />
          ) : (
            <>
              <h3 style={{ fontSize: 14, margin: "0 0 10px", color: "var(--text-primary)" }}>
                Upcoming classes (next {daysWindow} days){isFetching ? "" : ` · ${classes.length}`}
              </h3>
              {isFetching ? (
                <p className="bi-muted">Loading classes…</p>
              ) : classes.length === 0 ? (
                <EmptyState
                  title="No upcoming classes"
                  text={`No class needing this book starts in the next ${daysWindow} days.`}
                />
              ) : (
                <>
                  <ClassTable classes={classes} />
                  <div className="bi-class-total">
                    <span>Still needed ({daysWindow} days)</span>
                    <strong>{formatQty(total)}</strong>
                  </div>
                </>
              )}
              <p className="bi-note bi-note--icon">
                <Info size={16} aria-hidden />
                Each class receives books once, on the first day of the course. Actual and Estimated add a 30% student
                buffer; Estimated uses the current class&apos;s size, and — means there isn&apos;t enough data yet. Still
                needed = books needed − books already issued.
              </p>
            </>
          )}
        </div>
      )}
    </Drawer>
  )
}

export default DemandDetailDrawer
